import { compareByOrder, resultAttrInfo, resultPoints, resultValue, type FormulaCache, type PointsCache } from '@/orm/scoring'
import { DateFormat, ds } from '@/plugins/i18nFormat'
import EventBoat from '@model/EventBoat'
import EventRace from '@model/EventRace'
import { Model } from 'pinia-orm'
import { DateCast } from 'pinia-orm/casts'
import { Attr, BelongsTo, Cast, Num, Str, Uid } from 'pinia-orm/decorators'

/**
 * Result (finish) of a boat within a single elimination heat.
 *
 * Kept intentionally separate from EventRound / EventResult (which are tightly coupled to
 * the regular per-race scoring) so that round/heat tracking cannot interfere with it.
 */
export default class EventHeatResult extends Model {
    static entity = 'event-heat-results'

    @Uid() declare id: string
    @Uid() declare eventId: string      // only for sharding
    @Uid() declare eventRaceId: string
    @BelongsTo( () => EventRace, 'eventRaceId' ) declare race: EventRace
    @Uid() declare eventBoatId: string
    @BelongsTo( () => EventBoat, 'eventBoatId' ) declare boat: EventBoat
    @Num( 0 ) declare round: number     // 1-based round number within the elimination
    @Num( 0 ) declare heat: number      // 1-based heat (group) order within the round
    @Num( null ) declare order: number | null
    @Str( null ) declare other: string | null
    @Num( null ) declare attr: number | null
    @Cast( () => DateCast ) @Attr( '' ) declare ts: Date | null

    get dump () {
        return {
            eventBoatId: this.eventBoatId,
            _boat: { country: this.boat?.country, sailNumber: this.boat?.sailNumber },
            round: this.round,
            heat: this.heat,
            order: this.order,
            other: this.other,
            attr: this.attr,
            ts: this.ts === null ? null : ds( this.ts, DateFormat.System ),
        }
    }

    get result (): string | number | null {
        return resultValue( this.other, this.order )
    }

    set result ( val ) {
        // ignore
    }

    get points (): number {
        return this.calculatePoints()
    }

    declare __pointsCache?: PointsCache
    static __formulaCache: FormulaCache = new Map()

    // Same scoring as EventResult, but with the plain order (no handicap ever applies in a heat).
    // The boat count is the number of boats in this specific heat, not the whole race. The trailing
    // `true` marks heat scoring: an "other result" caps at the heat size (boats), not boats + 1.
    calculatePoints ( totalBoats: number | null = null ): number {
        const boats = totalBoats === null ? this.race.eliminationHeatBoatCount( this.boat?.boatClassId, this.round, this.heat ) : totalBoats

        const { value, cache } = resultPoints( this.order, this.order, this.other, this.attr, boats, EventHeatResult.__formulaCache, this.__pointsCache, true )

        this.__pointsCache = cache

        return value
    }

    // No handicap in a heat, so the finish-time comparison from EventResult is dropped.
    orderTo ( comparable: EventHeatResult ) {
        return compareByOrder( this.order, comparable.order, this.other, comparable.other )
    }

    get attrInfo () {
        return resultAttrInfo( this.attr, this.other )
    }

    static piniaOptions = {
        persist: false
    }
}
